import { useAuth, useSecureContext } from '@nebulr-group/nblocks-react';
import { useConfig } from '@nebulr-group/nblocks-react/lib/hooks/config-context';
import React, { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { StorageContext } from 'src/hooks/useLocalStorage';
import { APP_ID_KEY } from 'src/shared/Constants';
import { CloudViewsHandoverArgs, HandoverUtil } from 'src/utils/handover-util';

/**
 * Component for '/handover' route all initial browser requests will be targeting
 *
 * The following example will grab appId, store it in the localstorage and redirect to '/auth/set-password/1234567'
 * http://localhost:3090/handover?appId=633402fdf28d8e00252948b1&flow=full&args=eyJlbnRyeVBvaW50IjoiL2F1dGgvc2V0LXBhc3N3b3JkLzEyMzQ1NjcifQ==
 *
 */
function Handover() {
  const location = useLocation();
  const { appId: nblocksStorageAppId } = useConfig();

  const { appId, flow, args } = HandoverUtil.resolve(location.search);

  const storage: any = useContext(StorageContext);

  const { authService } = useSecureContext();
  const { logout } = useAuth();

  useEffect(() => {
    storage.set(APP_ID_KEY, appId);
    if (appId !== nblocksStorageAppId) {
      console.log(`Got another appId: ${appId} and ${nblocksStorageAppId}`);
      window.location.replace(`${window.location.href}`);
    } else {
      handleFlow();
    }
  }, [appId]);

  const generateErrorQueryParams = (
    errorDetails: CloudViewsHandoverArgs['errorDetails'],
  ): string => {
    if (!errorDetails) {
      return '/auth/login';
    } else {
      switch (errorDetails.type) {
        case 'FEDERATION_MISSING_USER':
          if (errorDetails.federation) {
            return `/auth/signup?error=missing_user&federation=${errorDetails.federation}`;
          } else {
            return `/auth/login`;
          }
        case 'MAGIC_LINK_EXPIRED':
          return '/auth/login?error=true&details=mle';
        default:
          return '/auth/login?error=true';
      }
    }
  };

  const handleFlow = async () => {
    switch (flow) {
      case 'openidconnect':
        await resetAuth();
        window.location.href = args.entryPoint
          ? args.entryPoint
          : `${generateErrorQueryParams(args.errorDetails)}`;
        break;
      case 'openidconnect_signup':
        await resetAuth();
        window.location.href = createSignupOptionsQuery(
          args.entryPoint ? args.entryPoint : '/auth/signup',
          args,
        );
        break;
      case 'ms-azure-marketplace-landing-page':
        await resetAuth();
        window.location.href = 'ms-marketplace-landing';
        break;
      case 'user-management-portal':
        await loginWithCode(args);
        window.location.href = '/user-management';
        break;
      case 'user-management-users':
        await loginWithCode(args);
        window.location.href = '/user-management/users';
        break;
      case 'user-management-workspace':
        await loginWithCode(args);
        window.location.href = '/user-management/workspace';
        break;
      case 'user-management-profile':
        await loginWithCode(args);
        window.location.href = '/user-management/profile';
        break;
      case 'select-plan':
        await loginWithCode(args);
        window.location.href = '/tenant/plan';
        break;
      case 'full':
      default:
        // Wait with handleAuth, a lot of links from account-api are using full
        //await handleAuth();
        window.location.href = args.entryPoint ? args.entryPoint : '/';
        break;
    }
  };

  const createSignupOptionsQuery = (
    url: string,
    args: CloudViewsHandoverArgs,
  ) => {
    console.log(args);
    if (!args.signupOptions) {
      return url;
    }

    const { signupPlan, signupCurrency, signupRecurrenceInterval } =
      args.signupOptions;
    return `${url}?${new URLSearchParams(
      JSON.parse(
        JSON.stringify({
          plan: signupPlan,
          currency: signupCurrency,
          recurrenceInterval: signupRecurrenceInterval,
        }),
      ),
    ).toString()}`;
  };

  const loginWithCode = async (args: { code?: string, redirectUri?: string }) => {
    const { code, redirectUri } = args;
    if (!code) {
      throw new Error("Can't init auth without a code");
    }

    console.log(`Logging in with code for appId: ${appId} and redirect uri ${redirectUri}`);

    // We give the react plugin the redirectUri context so it can resolve the code correctly
    // Otherwise it will not provide redirectUri to /token/code in auth-api and the auth-api will assume default redirectUri which is not always the case
    // Especially when running custom Oauth2.0/OIDC integration
    await authService.handleCallbackCode(code, { useShortHand: true, redirectUri: redirectUri });
  };

  const resetAuth = async () => {
    await logout(true);
  };

  return <></>;
}

export default Handover;
